import React from 'react';
import {
  Box,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ErrorBox,
  ErrorTitle,
  ErrorMessage,
} from '@marageti/z4-lib';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ResetForm from '../../../components/set-password-form';
import { ReactComponent as ChevronIcon } from '../../../assets/svg/chevron.svg';
import { ViewProps } from './types';

const titleSx = {
  fontFamily: 'Satoshi, sans-serif',
  fontWeight: 500,
  fontSize: '28px',
  lineHeight: '40px',
  letterSpacing: 0,
  textAlign: 'center',
  color: '#070707',
} as const;

const formSx = {
  width: '100%',
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  mt: '40px',
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
  },
  '& .MuiButton-contained': {
    py: 1.5,
    textTransform: 'none',
    borderRadius: 0,
    backgroundColor: '#070707',
    color: '#fff',
    fontWeight: 500,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#333',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: '#E6E3DF',
      color: '#B1AEAB',
      opacity: 1,
    },
  },
} as const;

const SetPasswordView = ({
  email,
  loginError,
  onSubmit,
}: ViewProps): React.ReactElement => {
  const navigate = useNavigate();
  const [search] = useSearchParams();

  const handleBackToLogin = () => {
    const params = new URLSearchParams(search);
    params.delete('token');
    params.delete('email');
    const query = params.toString();
    navigate(query ? `/login?${query}` : '/login');
  };

  return (
    <Box
      width="100%"
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack spacing={2} width="100%">
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <IconButton
            aria-label="Back to sign in"
            onClick={handleBackToLogin}
            sx={{
              color: '#070707',
              ml: -1,
              alignSelf: 'flex-start',
            }}
          >
            <ChevronIcon style={{ transform: 'rotate(180deg)', width: 20, height: 20 }} />
          </IconButton>
        </Box>

        <Stack spacing={2} alignItems="center" width="100%">
          <Typography component="h1" sx={titleSx}>
            Create a New Password
          </Typography>
          <Typography color="text.secondary" textAlign="center">
            Use at least 8 characters
          </Typography>
        </Stack>
      </Stack>

      <Box sx={formSx}>
        <ResetForm email={email} onSubmit={onSubmit} />
      </Box>

      {loginError && (
        <Box sx={{ mt: 3 }}>
          <ErrorBox>
            <ErrorTitle>
              There was an on our end.
            </ErrorTitle>
            {loginError.code ? (
              <ErrorMessage>
                {loginError.code && `Code: ${loginError.code} `}
                {loginError.message || 'Please contact support'}
                <br />
                {loginError.id && `ID: ${loginError.id}`}
              </ErrorMessage>
            ) : (
              <ErrorMessage>
                Please contact support.
              </ErrorMessage>
            )}
          </ErrorBox>
        </Box>
      )}
    </Box>
  );
};

export default SetPasswordView;
